import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import apiService from '../../services/api'
import { useAuthStore } from '../../store/authStore'

interface GovAgencyRegisterFormData {
  agencyName: string
  email: string
  website: string
  contactPersonName: string
  agencyType: string
  document: File | null
  authorizedRepresentative: boolean
}

const AGENCY_TYPES = [
  { value: 'city', label: 'City Government' },
  { value: 'utility', label: 'Public Utility' },
  { value: 'university', label: 'University/Educational Institution' },
  { value: 'other', label: 'Other Government Agency' }
]

const GOVERNMENT_DOMAINS = [
  '.gov',
  '.edu',
  '.mil',
  '.us',
  '.state.',
  '.city.',
  '.county.',
  '.k12.',
  '.cc.',
  '.org' // Some government agencies use .org
]

export default function GovAgencyRegisterForm() {
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const [formData, setFormData] = useState<GovAgencyRegisterFormData>({
    agencyName: '',
    email: '',
    website: '',
    contactPersonName: '',
    agencyType: '',
    document: null,
    authorizedRepresentative: false
  })
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [domainWarning, setDomainWarning] = useState('')

  // Check if user has already submitted a government agency registration
  // Allow resubmission if status is REJECTED
  const hasSubmittedRegistration = Boolean(user?.govStatus && user.govStatus !== 'NONE' && user.govStatus !== 'REJECTED')

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return 'Please enter a valid email address';
    }

    const domain = email.toLowerCase();
    const isGovernmentDomain = GOVERNMENT_DOMAINS.some(govDomain => 
      domain.includes(govDomain)
    );

    if (!isGovernmentDomain) {
      setDomainWarning('Email domain does not appear to be from a government or educational institution. Your application will require manual review.');
    } else {
      setDomainWarning('');
    }

    return '';
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.agencyName.trim()) {
      newErrors.agencyName = 'Agency name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else {
      const emailError = validateEmail(formData.email);
      if (emailError) {
        newErrors.email = emailError;
      }
    }

    if (!formData.contactPersonName.trim()) {
      newErrors.contactPersonName = 'Contact person name is required';
    }

    if (!formData.agencyType) {
      newErrors.agencyType = 'Agency type is required';
    }

    if (!formData.document) {
      newErrors.document = 'Please upload a government document for verification';
    } else {
      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/gif'];
      if (!allowedTypes.includes(formData.document.type)) {
        newErrors.document = 'Please upload a PDF or image file';
      }
      if (formData.document.size > 10 * 1024 * 1024) { // 10MB limit
        newErrors.document = 'File size must be less than 10MB';
      }
    }

    if (!formData.authorizedRepresentative) {
      newErrors.authorizedRepresentative = 'You must confirm you are authorized to represent this agency';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }

    // Validate email domain in real-time
    if (name === 'email' && value) {
      validateEmail(value);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setFormData(prev => ({
      ...prev,
      document: file
    }));

    if (errors.document) {
      setErrors(prev => ({
        ...prev,
        document: ''
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!validateForm()) {
      return
    }

    setIsSubmitting(true)

    try {
      const submitData = new FormData()
      submitData.append('agencyName', formData.agencyName)
      submitData.append('email', formData.email)
      submitData.append('website', formData.website)
      submitData.append('contactPersonName', formData.contactPersonName)
      submitData.append('agencyType', formData.agencyType)
      submitData.append('authorizedRepresentative', formData.authorizedRepresentative.toString())
      
      if (formData.document) {
        submitData.append('document', formData.document)
      }

      await apiService.post('/api/gov/register', submitData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })

      // Store the email in localStorage to track this registration
      localStorage.setItem('gov_agency_email', formData.email)

      // Redirect to success page or login
      navigate('/gov/register-success', { 
        state: { 
          message: 'Your agency registration has been submitted for review. You will receive an email notification once approved.' 
        } 
      })
    } catch (error: any) {
      console.error('Registration error:', error)
      const errorMessage = error.response?.data?.message || 'Network error. Please check your connection and try again.'
      setErrors({ submit: errorMessage })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="max-w-2xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-lg p-8">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Government Agency Registration
          </h1>
          <p className="text-gray-600">
            Register your government agency to access Evoltbay's procurement platform
          </p>
        </div>

        {hasSubmittedRegistration ? (
          <div className="text-center py-8">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100 mb-4">
              <svg className="h-6 w-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Registration Under Review
            </h2>
            <p className="text-gray-600 mb-4">
              Your government agency registration has already been submitted and is currently under review.
            </p>
            <p className="text-sm text-gray-500 mb-6">
              Status: {user?.govStatus === 'PENDING' ? 'Pending Approval' : user?.govStatus}
            </p>
            <div className="space-y-3">
              <button
                onClick={() => navigate('/login')}
                className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                Go to Login
              </button>
              <button
                onClick={() => navigate('/')}
                className="w-full bg-gray-100 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
              >
                Back to Home
              </button>
            </div>
          </div>
        ) : user?.govStatus === 'REJECTED' ? (
          <div className="text-center py-8">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
              <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Previous Application Rejected
            </h2>
            <p className="text-gray-600 mb-4">
              Your previous government agency registration was not approved. You may submit a new application with updated information.
            </p>
            <p className="text-sm text-gray-500 mb-6">
              Please review your application details and ensure all information is accurate before resubmitting.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
          {/* Agency Name */}
          <div>
            <label htmlFor="agencyName" className="block text-sm font-medium text-gray-700 mb-2">
              Agency Name *
            </label>
            <input
              type="text"
              id="agencyName"
              name="agencyName"
              value={formData.agencyName}
              onChange={handleInputChange}
              className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.agencyName ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Enter your agency name"
            />
            {errors.agencyName && (
              <p className="mt-1 text-sm text-red-600">{errors.agencyName}</p>
            )}
          </div>

          {/* Email */}
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
              Official Email Address *
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.email ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="agency@example.gov"
            />
            {errors.email && (
              <p className="mt-1 text-sm text-red-600">{errors.email}</p>
            )}
            {domainWarning && (
              <p className="mt-1 text-sm text-yellow-600 flex items-center">
                <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                {domainWarning}
              </p>
            )}
          </div>

          {/* Website */}
          <div>
            <label htmlFor="website" className="block text-sm font-medium text-gray-700 mb-2">
              Agency Website (Optional)
            </label>
            <input
              type="url"
              id="website"
              name="website"
              value={formData.website}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="https://agency.example.gov"
            />
          </div>

          {/* Contact Person */}
          <div>
            <label htmlFor="contactPersonName" className="block text-sm font-medium text-gray-700 mb-2">
              Contact Person Name *
            </label>
            <input
              type="text"
              id="contactPersonName"
              name="contactPersonName"
              value={formData.contactPersonName}
              onChange={handleInputChange}
              className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.contactPersonName ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Enter contact person's full name"
            />
            {errors.contactPersonName && (
              <p className="mt-1 text-sm text-red-600">{errors.contactPersonName}</p>
            )}
          </div>

          {/* Agency Type */}
          <div>
            <label htmlFor="agencyType" className="block text-sm font-medium text-gray-700 mb-2">
              Type of Agency *
            </label>
            <select
              id="agencyType"
              name="agencyType"
              value={formData.agencyType}
              onChange={handleInputChange}
              className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.agencyType ? 'border-red-500' : 'border-gray-300'
              }`}
            >
              <option value="">Select agency type</option>
              {AGENCY_TYPES.map(type => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
            {errors.agencyType && (
              <p className="mt-1 text-sm text-red-600">{errors.agencyType}</p>
            )}
          </div>

          {/* Document Upload */}
          <div>
            <label htmlFor="document" className="block text-sm font-medium text-gray-700 mb-2">
              Government Document *
            </label>
            <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
              <div className="space-y-1 text-center">
                <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                  <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <div className="flex text-sm text-gray-600">
                  <label htmlFor="document" className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500">
                    <span>Upload a file</span>
                    <input
                      id="document"
                      name="document"
                      type="file"
                      className="sr-only"
                      accept=".pdf,.jpg,.jpeg,.png,.gif"
                      onChange={handleFileChange}
                    />
                  </label>
                  <p className="pl-1">or drag and drop</p>
                </div>
                <p className="text-xs text-gray-500">PDF, PNG, JPG, GIF up to 10MB</p>
              </div>
            </div>
            {formData.document && (
              <p className="mt-2 text-sm text-gray-600">
                Selected: {formData.document.name}
              </p>
            )}
            {errors.document && (
              <p className="mt-1 text-sm text-red-600">{errors.document}</p>
            )}
            <p className="mt-2 text-xs text-gray-500">
              Please upload an official government document (letterhead, ID, certificate, etc.) to verify your agency affiliation.
            </p>
          </div>

          {/* Authorization Checkbox */}
          <div className="flex items-start">
            <div className="flex items-center h-5">
              <input
                id="authorizedRepresentative"
                name="authorizedRepresentative"
                type="checkbox"
                checked={formData.authorizedRepresentative}
                onChange={handleInputChange}
                className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 rounded"
              />
            </div>
            <div className="ml-3 text-sm">
              <label htmlFor="authorizedRepresentative" className="font-medium text-gray-700">
                I am authorized to represent this agency *
              </label>
              <p className="text-gray-500">
                By checking this box, you confirm that you have the authority to register this agency on Evoltbay's procurement platform.
              </p>
            </div>
          </div>
          {errors.authorizedRepresentative && (
            <p className="mt-1 text-sm text-red-600">{errors.authorizedRepresentative}</p>
          )}

          {/* Submit Error */}
          {errors.submit && (
            <div className="rounded-md bg-red-50 p-4">
              <p className="text-sm text-red-600">{errors.submit}</p>
            </div>
          )}

          {/* Submit Button */}
          <div>
            <button
              type="submit"
              disabled={isSubmitting || hasSubmittedRegistration}
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <div className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Submitting...
                </div>
              ) : hasSubmittedRegistration ? (
                'Registration Already Submitted'
              ) : (
                'Submit Registration'
              )}
            </button>
          </div>
        </form>
        )}

        <div className="mt-6 text-center">
          <p className="text-sm text-gray-600">
            Already have an account?{' '}
            <button
              onClick={() => navigate('/login')}
              className="font-medium text-blue-600 hover:text-blue-500"
            >
              Sign in here
            </button>
          </p>
        </div>
      </div>
    </div>
  );
} 